import { Trans } from '@lingui/macro';
import { useHistory } from '../../hooks/useRouterCompat';
import { My } from '../../services/My';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Rocket } from 'lucide-react';

const UnavailablePage: React.FC = () => {
  const history = useHistory();

  function login() {
    history.replace('/login');
  }

  function gotoRoot() {
    window.location.href = '/';
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-page-dark text-page-dark-text relative overflow-hidden font-sans selection:bg-purple-500/30">

      {/* Stars Background Effect (CSS-based simple stars or just dots) */}
      <div className="absolute inset-0 z-0">
        <div className="absolute h-1 w-1 bg-background rounded-full top-10 left-10 animate-pulse opacity-70"></div>
        <div className="absolute h-1 w-1 bg-background rounded-full top-20 right-20 animate-pulse delay-75 opacity-50"></div>
        <div className="absolute h-0.5 w-0.5 bg-background rounded-full bottom-40 left-1/4 animate-pulse delay-150 opacity-80"></div>
        <div className="absolute h-1 w-1 bg-blue-200 rounded-full top-1/3 left-1/3 animate-pulse delay-300 opacity-60"></div>
        <div className="absolute h-0.5 w-0.5 bg-background rounded-full bottom-10 right-10 animate-pulse delay-500 opacity-40"></div>

        {/* Nebula/Gradient overlays */}
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-purple-900/20 blur-[100px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-900/20 blur-[120px] rounded-full pointer-events-none"></div>
      </div>

      <div className="z-10 flex flex-col items-center text-center p-8 max-w-lg w-full">
        {/* Error Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background/5 border border-white/10 backdrop-blur-sm mb-6">
          <span className="w-2 h-2 rounded-full bg-destructive animate-pulse"></span>
          <span className="text-xs font-bold tracking-widest text-muted-foreground">404 ERROR</span>
        </div>

        {/* Main Title */}
        <h1 className="text-2xl md:text-3xl font-black mb-8 tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-white via-gray-200 to-gray-500 shadow-2xl">
          <Trans>Page Not Found</Trans>
        </h1>

        <div className="mb-10 space-y-4 max-w-xs mx-auto">
          {My.isLoggedIn() ? (
            <p className="text-muted-foreground text-sm leading-relaxed">
              <Trans>The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.</Trans>
            </p>
          ) : (
            <p className="text-muted-foreground text-sm leading-relaxed">
              <Trans>You seem to have drifted away. You may need to sign in to access this sector.</Trans>
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
          {!My.isLoggedIn() ? (
            <Button
              onClick={login}
              className="h-12 px-8 rounded-full bg-background text-foreground hover:bg-accent font-semibold transition-all hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(255,255,255,0.3)]"
            >
              <Trans>Sign In</Trans>
            </Button>
          ) : (
            <Button
              onClick={gotoRoot}
              variant="outline"
              className="h-12 px-8 rounded-full border-page-dark-text/20 text-page-dark-text hover:bg-background/10 hover:text-page-dark-text hover:border-page-dark-text/40 transition-all gap-2 group backdrop-blur-sm bg-background/5"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
              <Trans>Return Home</Trans>
            </Button>
          )}
        </div>
      </div>

      {/* Floating Astronaut */}
      <div className="absolute bottom-12 right-6 md:right-20 md:bottom-20 z-0 opacity-90 pointer-events-none animate-[float_6s_ease-in-out_infinite]">
        <img
          src="assets/img/img-cover-floating-160.png"
          alt="Astronaut"
          className="w-24 md:w-40 h-auto drop-shadow-[0_10px_20px_rgba(0,0,0,0.5)] rotate-12"
        />
        {/* Rocket Icon Decoration */}
        <div className="absolute top-[-20px] left-[-30px] opacity-0 animate-[fade-in-out_4s_ease-in-out_infinite_1s]">
          <Rocket className="w-6 h-6 text-page-dark-text/50 rotate-45" />
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(12deg); }
          50% { transform: translateY(-20px) rotate(8deg); }
        }
      `}</style>
    </div>
  );
};

export default UnavailablePage;